import React from 'react';
import {
  Box,
  Typography,
  Container,
  Stack,
  Grid,
  Paper,
  Button,
  Chip,
} from '@mui/material';

const About = () => {
  const imageUrls = [
    'aws.png',
    'openai.png',
    'anthropic.png',
    'opencart.png',
    'docker.png',
    'jenkins.png',
    'owasp.png',
  ];

  return (
    <Box
      id="about"
      sx={{
        pt: { xs: 3, sm: 4 },
        pb: { xs: 6, md: 9 },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: 4, md: 6 },
                px: { xs: 2.5, sm: 4, md: 5 },
                py: { xs: 4, sm: 5, md: 6 },
                minHeight: '100%',
                background:
                  'linear-gradient(145deg, rgba(20, 50, 92, 1) 0%, rgba(24, 76, 123, 0.98) 48%, rgba(32, 198, 183, 0.9) 100%)',
                color: '#fff',
                boxShadow: '0 32px 80px rgba(20, 50, 92, 0.25)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 'auto -10% -25% auto',
                  width: { xs: 180, md: 240 },
                  height: { xs: 180, md: 240 },
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.12)',
                  filter: 'blur(4px)',
                }}
              />
              <Chip
                label="Digital products engineered for real-world growth"
                sx={{
                  mb: 2.5,
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  color: '#fff',
                  fontWeight: 700,
                  backdropFilter: 'blur(8px)',
                }}
              />
              <Typography
                variant="h1"
                component="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.25rem', md: '4.4rem' },
                  mb: 2,
                  maxWidth: '10ch',
                }}
              >
                We build modern software with practical AI.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.12rem' },
                  color: 'rgba(255,255,255,0.84)',
                  maxWidth: 640,
                  mb: 3,
                }}
              >
                QuantumMind Software designs secure, scalable platforms for companies that need
                smarter digital workflows, dependable infrastructure, and polished customer
                experiences.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  color="secondary"
                  component="a"
                  href="/#services"
                  sx={{
                    color: '#08243f',
                    py: 1.35,
                  }}
                >
                  Explore Services
                </Button>
                <Button
                  variant="outlined"
                  component="a"
                  href="/#contact"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.42)',
                    color: '#fff',
                    py: 1.35,
                    '&:hover': {
                      borderColor: '#fff',
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  Contact the Team
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={3} sx={{ height: '100%' }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: { xs: 4, md: 5 },
                  p: { xs: 2.5, sm: 3.5 },
                  backgroundColor: 'rgba(255,255,255,0.84)',
                  backdropFilter: 'blur(18px)',
                  border: '1px solid rgba(255,255,255,0.6)',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: 'secondary.main', letterSpacing: '0.14em', fontWeight: 700 }}
                >
                  About Us
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    mt: 0.6,
                    mb: 1.6,
                    color: 'primary.main',
                  }}
                >
                  Full-cycle delivery with a calm, technical edge.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  From discovery to deployment, we combine cloud architecture, API-driven systems,
                  and AI-enabled automation to ship solutions that stay maintainable as they grow.
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: { xs: 4, md: 5 },
                  p: { xs: 2.5, sm: 3.5 },
                  backgroundColor: '#ffffff',
                  border: '1px solid rgba(16, 35, 63, 0.08)',
                  boxShadow: '0 20px 50px rgba(16, 35, 63, 0.08)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5 }}
                >
                  Trusted technology stack
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
                  We work with proven platforms to keep performance, deployment, and security
                  standards high.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ justifyContent: { xs: 'flex-start', md: 'center' } }}
                >
                  {imageUrls.map((url, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={url}
                      alt={`Tech Partner ${index + 1}`}
                      sx={{
                        width: { xs: 64, sm: 72 },
                        height: { xs: 64, sm: 72 },
                        objectFit: 'contain',
                        borderRadius: 3,
                        p: 1.2,
                        backgroundColor: '#f5f8fc',
                        border: '1px solid rgba(20, 50, 92, 0.08)',
                      }}
                    />
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
